import React from 'react'
import { Label, Checkbox } from '@pcln/horizon'
import { Discount } from 'pcln-icons'

type ProductType = 'hotels' | 'flights' | 'cars'

// Individual option objects (similar to BundleAndSave/utils.ts)
export const HotelOption = {
  id: 'addHotel',
  name: 'addHotel',
  label: 'Add a hotel',
  value: 'STAY',
  type: 'STAY'
} as const

const FlightOption = {
  id: 'addFlights',
  name: 'addFlights',
  label: 'Add a flight',
  value: 'FLY',
  type: 'FLY'
} as const

const CarOption = {
  id: 'addCar',
  name: 'addCar',
  label: 'Add a car',
  value: 'DRIVE',
  type: 'DRIVE'
} as const

type BundleOption = typeof HotelOption | typeof FlightOption | typeof CarOption

type HorizonBundleAndSaveProps = {
  productType: ProductType
  options?: ReadonlyArray<BundleOption>
  checkedValues: Record<string, boolean>
  onSelectionChange: (
    updatedTripType: string,
    checked: boolean,
    optionValue: string
  ) => void
  label?: string
  className?: string
  tripType: string
}

// Product-specific configurations
const productConfigs = {
  hotels: {
    defaultOptions: [FlightOption, CarOption],
    defaultLabel: 'Package Deals'
  },
  flights: {
    defaultOptions: [HotelOption, CarOption],
    defaultLabel: 'Bundle + Save'
  },
  cars: {
    defaultOptions: [HotelOption, FlightOption],
    defaultLabel: 'Bundle + Save'
  }
} as const

export default function HorizonBundleAndSave({
  productType,
  options,
  checkedValues,
  onSelectionChange,
  label,
  className = '',
  tripType
}: HorizonBundleAndSaveProps) {
  const config = productConfigs[productType]
  const defaultOptions = options || config.defaultOptions
  const defaultLabel = label || config.defaultLabel

  function getUpdatedTripType(checked: boolean, option: BundleOption) {
    return checked
      ? `${tripType}:${option.type}`
      : tripType.replace(`:${option.type}`, '')
  }

  return (
    <div className={`flex flex-row gap-4 ${className}`}>
      <div className="flex items-center gap-1.5">
        <Discount size={24} color="success" />
        <span className="text-body3 font-medium text-success-dark">
          {defaultLabel}
        </span>
      </div>
      <div className="flex flex-row gap-4">
        {defaultOptions.map(option => {
          const checked = checkedValues[option.value] || false
          return (
            <Label
              key={option.id}
              htmlFor={option.id}
              className="flex items-center"
            >
              <Checkbox
                size="16"
                id={option.id}
                value={option.value}
                name={option.name}
                checked={checked}
                handleChange={() => {
                  const newCheckedState = !checked
                  const updatedTripType = getUpdatedTripType(
                    newCheckedState,
                    option
                  )
                  onSelectionChange(
                    updatedTripType,
                    newCheckedState,
                    option.value
                  )
                }}
              />
              <span className="ml-2 text-body3">{option.label}</span>
            </Label>
          )
        })}
      </div>
    </div>
  )
}
